import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FileText, MoreVertical, Eye, Edit, Trash2, Archive, Plus,
  ArrowUp, ArrowDown,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const statusLabels = {
  draft: "Draft", under_review: "Under Review", pending_approval: "Pending Approval",
  approved: "Approved", active: "Active", expired: "Expired",
  terminated: "Terminated", archived: "Archived",
};

const statusColors = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  under_review: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  pending_approval: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  active: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  expired: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  terminated: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
  archived: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};

const GDPR_PROTECTED = ["approved", "active", "expired"];
const IN_WORKFLOW = ["under_review", "pending_approval"];

const formatDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const formatCurrency = (v, ccy) => {
  if (!v && v !== 0) return "-";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy || "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${ccy || ""} ${Number(v).toLocaleString()}`;
  }
};

export function ContractTable({
  contracts, sortField, sortDir, onSort, onArchive, onDeleteClick,
}) {
  const navigate = useNavigate();
  const isProtected = (c) => GDPR_PROTECTED.includes(c?.status);
  const isInWorkflow = (c) => IN_WORKFLOW.includes(c?.status);

  const SortHeader = ({ field, children }) => (
    <th className="cursor-pointer select-none" onClick={() => onSort(field)} data-testid={`sort-${field}`}>
      <span className="inline-flex items-center gap-1">
        {children} {sortField === field && (sortDir === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />)}
      </span>
    </th>
  );

  if (contracts.length === 0) {
    return (
      <div className="text-center py-20">
        <FileText size={48} className="mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500 mb-4">No contracts found</p>
        <Link to="/contracts/new/wizard">
          <Button className="gap-2 rounded-sm" data-testid="empty-create-btn"><Plus size={18} />Create Contract</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="contracts-table">
        <thead>
          <tr>
            <th>Contract ID</th>
            <SortHeader field="title">Contract</SortHeader>
            <th>Supplier ID</th>
            <th>Supplier</th>
            <th>Type</th>
            <SortHeader field="spend_category">Spend Category</SortHeader>
            <SortHeader field="jurisdiction">Jurisdiction</SortHeader>
            <SortHeader field="contract_value">Value</SortHeader>
            <th>Addenda</th>
            <SortHeader field="expiration_date">Expiration</SortHeader>
            <SortHeader field="status">Status</SortHeader>
            <th className="w-12"></th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((contract) => (
            <tr key={contract.id} data-testid={`contract-row-${contract.id}`}>
              <td className="text-sm font-mono text-slate-500" data-testid={`contract-uid-${contract.id}`}>
                {contract.custom_fields?.contract_unique_id || "-"}
              </td>
              <td>
                <Link to={`/contracts/${contract.id}`} className="font-medium text-slate-900 dark:text-white hover:text-cyan-600 dark:hover:text-cyan-400">
                  {contract.title}
                </Link>
                {contract.custom_fields?.bulk_imported && <Badge variant="outline" className="ml-2 text-xs">Imported</Badge>}
              </td>
              <td className="text-sm font-mono text-slate-500" data-testid={`supplier-uid-${contract.id}`}>
                {contract.custom_fields?.supplier_unique_id || "-"}
              </td>
              <td className="text-slate-600 dark:text-slate-400">{contract.contracting_party?.legal_name || "-"}</td>
              <td className="text-slate-600 dark:text-slate-400">{contract.contract_type || "-"}</td>
              <td className="text-slate-600 dark:text-slate-400" data-testid={`spend-category-${contract.id}`}>{contract.spend_category || "-"}</td>
              <td className="text-slate-600 dark:text-slate-400" data-testid={`jurisdiction-${contract.id}`}>{contract.jurisdiction || "-"}</td>
              <td className="font-mono text-sm">{formatCurrency(contract.contract_value, contract.currency)}</td>
              <td>
                {contract.addenda_count > 0 ? (
                  <Link to={`/contracts/${contract.id}?tab=addenda`} className="text-cyan-600 hover:text-cyan-700 font-medium text-sm" data-testid={`addenda-link-${contract.id}`}>
                    {contract.addenda_count} addend{contract.addenda_count === 1 ? "um" : "a"}
                  </Link>
                ) : <span className="text-slate-400 text-sm">-</span>}
              </td>
              <td className="text-slate-600 dark:text-slate-400">{formatDate(contract.expiration_date)}</td>
              <td>
                <Badge className={`rounded-sm ${statusColors[contract.status]}`}>
                  {statusLabels[contract.status] || contract.status}
                </Badge>
              </td>
              <td>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`contract-menu-${contract.id}`}>
                      <MoreVertical size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.id}`)}>
                      <Eye size={16} className="mr-2" />View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.id}/edit`)}>
                      <Edit size={16} className="mr-2" />Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onArchive(contract)}>
                      <Archive size={16} className="mr-2" />Archive
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDeleteClick(contract)}
                      className={isProtected(contract) || isInWorkflow(contract) ? "text-slate-400 cursor-not-allowed" : "text-red-600"}
                      disabled={isProtected(contract) || isInWorkflow(contract)}
                    >
                      <Trash2 size={16} className="mr-2" />
                      {isProtected(contract) ? "Delete (GDPR Protected)" : isInWorkflow(contract) ? "Delete (In Workflow)" : "Delete"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
